(() => {
  'use strict';

  const VERSION = '8.0.13';
  const root = () => window.Shopify?.routes?.root || '/';
  const normalize = (value) => String(value || '').trim().toLowerCase();
  const escapeHtml = (value) => String(value).replace(/[&<>'"]/g, (character) => ({
    '&':'&amp;', '<':'&lt;', '>':'&gt;', "'":'&#39;', '"':'&quot;'
  })[character]);
  const money = (cents, currency = 'USD') => new Intl.NumberFormat('en-US', {
    style: 'currency', currency, minimumFractionDigits: 2
  }).format((Number(cents) || 0) / 100);

  function readData(editor) {
    try { return JSON.parse(editor.querySelector('[data-me-size-editor-data]')?.textContent || '{}'); }
    catch (error) { console.error(`[Moments v${VERSION}] Invalid editor data.`, error); return {}; }
  }

  function variantOptions(variant) {
    return Array.isArray(variant.options)
      ? variant.options
      : [variant.option1, variant.option2, variant.option3].filter((value) => value != null);
  }

  function editorVariants(data) {
    const names = Array.isArray(data.productOptions) ? data.productOptions : [];
    let sizeIndex = names.findIndex((name) => /size/i.test(String(name)));
    const colorIndex = names.findIndex((name) => /colou?r/i.test(String(name)));
    if (sizeIndex < 0) sizeIndex = names.length > 1 ? 1 : 0;
    const selectedColor = normalize(data.color);
    const variants = (Array.isArray(data.variants) ? data.variants : []).filter((variant) => {
      if (colorIndex < 0 || !selectedColor) return true;
      return normalize(variantOptions(variant)[colorIndex]) === selectedColor;
    });
    return { sizeIndex, variants };
  }

  async function request(url, options = {}, timeoutMs = 15000) {
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), timeoutMs);
    try {
      const response = await fetch(url, { ...options, signal: controller.signal });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || payload.errors) {
        throw new Error(payload.description || payload.message || payload.errors || 'Shopify could not update the cart.');
      }
      return payload;
    } catch (error) {
      if (error?.name === 'AbortError') throw new Error('The cart update took too long. Please try again.');
      throw error;
    } finally {
      window.clearTimeout(timer);
    }
  }

  const cartState = () => request(`${root()}cart.js`, { headers: { Accept: 'application/json' } });
  const projectLines = (cart, groupId) => (cart.items || []).filter((line) => line.properties?.['_ME Design Group'] === groupId);
  const garmentLines = (cart, groupId) => projectLines(cart, groupId).filter((line) => line.properties?.['_ME Line Type'] === 'garment');
  const lineSize = (line) => String(line.properties?.['Garment Size'] || line.variant_title || '').trim();

  function currentRows(editor) {
    return [...editor.querySelectorAll('[data-me-current-size-row]')].map((row) => ({
      size: row.dataset.size || '',
      key: row.dataset.lineKey || '',
      variantId: Number(row.dataset.variantId || 0),
      original: Number(row.dataset.originalQuantity || 0),
      quantity: Math.max(0, Number.parseInt(row.querySelector('[data-me-size-input]')?.value || '0', 10) || 0)
    }));
  }

  function pendingAddition(editor) {
    const select = editor.querySelector('[data-me-add-size-select]');
    const input = editor.querySelector('[data-me-add-size-input]');
    const quantity = Math.max(0, Number.parseInt(input?.value || '0', 10) || 0);
    if (!select?.value || quantity < 1) return null;
    const option = select.selectedOptions[0];
    return { size: option.dataset.size || option.textContent.trim(), variantId: Number(select.value), quantity };
  }

  function setBusy(editor, busy, message = '') {
    editor.dataset.meBusy = busy ? 'true' : 'false';
    editor.querySelectorAll('button, input, select').forEach((control) => { control.disabled = busy; });
    const button = editor.querySelector('[data-me-size-editor-update]');
    if (button) button.textContent = busy ? 'Saving…' : 'Save changes';
    const status = editor.querySelector('[data-me-size-editor-status]');
    if (status) status.textContent = message;
  }

  function updatedProperties(properties, distribution, size, quantity, groupId) {
    const next = { ...(properties || {}) };
    const total = distribution.reduce((sum, line) => sum + Number(line.quantity || 0), 0);
    next['Order Type'] = 'Multiple Sizes';
    next['Size Distribution'] = distribution.map((line) => `${line.size}: ${line.quantity}`).join(', ');
    next['Project Total Pieces'] = String(total);
    next['_ME Design Group'] = String(groupId || next['_ME Design Group'] || '').trim();
    next['_ME Line Type'] = 'garment';
    next['Garment Size'] = size;
    next['_ME Size Quantity'] = String(quantity);
    return next;
  }

  async function renderEditor(editor, suppliedCart = null) {
    const data = readData(editor);
    const currentContainer = editor.querySelector('[data-me-current-sizes]');
    const select = editor.querySelector('[data-me-add-size-select]');
    const status = editor.querySelector('[data-me-size-editor-status]');
    if (!data.groupId || !currentContainer || !select) return;

    if (!suppliedCart) status.textContent = 'Loading sizes…';
    try {
      const cart = suppliedCart || await cartState();
      const lines = garmentLines(cart, data.groupId);
      const { sizeIndex, variants } = editorVariants(data);
      const variantBySize = new Map();
      variants.forEach((variant) => {
        const size = String(variantOptions(variant)[sizeIndex] || variant.title || '').trim();
        if (size) variantBySize.set(normalize(size), { ...variant, size });
      });
      const prices = variants.map((variant) => Number(variant.price || 0)).filter(Number.isFinite);
      const basePrice = prices.length ? Math.min(...prices) : 0;

      currentContainer.innerHTML = lines.map((line) => {
        const size = lineSize(line);
        const variant = variantBySize.get(normalize(size));
        const priceDiff = variant ? Number(variant.price || 0) - basePrice : 0;
        return `<div class="me-cart-size-editor__current-row" data-me-current-size-row data-size="${escapeHtml(size)}" data-line-key="${escapeHtml(line.key)}" data-variant-id="${line.variant_id}" data-original-quantity="${line.quantity}">
          <div class="me-cart-size-editor__size"><strong>${escapeHtml(size)}</strong>${priceDiff > 0 ? `<span>+${money(priceDiff, data.currency)}</span>` : ''}</div>
          <div class="me-cart-size-editor__stepper">
            <button type="button" data-me-size-minus aria-label="Remove one ${escapeHtml(size)}">−</button>
            <input type="number" min="0" step="1" value="${line.quantity}" data-me-size-input aria-label="${escapeHtml(size)} quantity">
            <button type="button" data-me-size-plus aria-label="Add one ${escapeHtml(size)}">+</button>
          </div>
        </div>`;
      }).join('');

      const used = new Set(lines.map((line) => normalize(lineSize(line))));
      const unused = [...variantBySize.values()].filter((variant) => !used.has(normalize(variant.size)) && variant.available !== false);
      select.innerHTML = unused.length
        ? `<option value="">Choose a size</option>${unused.map((variant) => {
            const diff = Number(variant.price || 0) - basePrice;
            return `<option value="${variant.id}" data-size="${escapeHtml(variant.size)}">${escapeHtml(variant.size)}${diff > 0 ? ` (+${money(diff, data.currency)})` : ''}</option>`;
          }).join('')}`
        : '<option value="">All available sizes are already included</option>';
      select.disabled = !unused.length;
      const addInput = editor.querySelector('[data-me-add-size-input]');
      if (addInput) addInput.value = '1';
      status.textContent = '';
    } catch (error) {
      console.error(`[Moments v${VERSION}] Loading sizes failed.`, error);
      status.textContent = error.message || 'Sizes could not be loaded.';
    }
  }

  function setOpen(editor, open) {
    const panel = editor.querySelector('[data-me-size-editor-panel]');
    const toggle = editor.querySelector('[data-me-size-editor-toggle]');
    if (!panel || !toggle) return;
    panel.hidden = !open;
    toggle.setAttribute('aria-expanded', String(open));
    editor.classList.toggle('is-open', open);
    if (open) renderEditor(editor);
  }

  async function updateLines(updates) {
    return request(`${root()}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates })
    }, 30000);
  }

  async function addLine(item) {
    return request(`${root()}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [item] })
    });
  }

  function groupOrderSavings(cart) {
    return (cart.cart_level_discount_applications || []).reduce((sum, discount) => {
      const title = String(discount?.title || '').toLowerCase();
      if (!(title.includes('group') && title.includes('saving'))) return sum;
      return sum + Number(discount?.total_allocated_amount || 0);
    }, 0);
  }

  function cartMetrics(cart) {
    const garments = (cart.items || []).filter((line) => line.properties?.['_ME Line Type'] === 'garment' && line.properties?.['_ME Design Group']);
    const quantity = garments.reduce((sum, line) => sum + Number(line.quantity || 0), 0);
    const original = garments.reduce((sum, line) => sum + Number(line.original_line_price || line.line_price || 0), 0);
    const finalBeforeOrderDiscount = garments.reduce((sum, line) => sum + Number(line.final_line_price ?? line.line_price ?? 0), 0);
    const tier = quantity >= 21 ? 0 : quantity >= 11 ? 10 : quantity >= 6 ? 5 : 0;
    const expectedGroupSavings = tier > 0 ? Math.floor(Math.max(0, finalBeforeOrderDiscount) * tier / 100) : 0;
    const actualGroupSavings = groupOrderSavings(cart);
    const quoteRequired = quantity >= 21;
    const effectiveGroupSavings = quoteRequired ? 0 : (actualGroupSavings > 0 ? actualGroupSavings : expectedGroupSavings);
    const productSavings = Math.max(0, original - finalBeforeOrderDiscount);
    const savings = productSavings + effectiveGroupSavings;
    const final = Math.max(0, finalBeforeOrderDiscount - effectiveGroupSavings);
    return { quantity, original, final, savings, tier, expectedGroupSavings, actualGroupSavings };
  }

  function isNonPhysicalProjectLine(line) {
    const lineType = normalize(line?.properties?.['_ME Line Type']);
    const groupId = String(line?.properties?.['_ME Design Group'] || '').trim();

    // A grouped project line is physical only when it is the garment itself.
    // This also excludes legacy/free customization lines whose line type may
    // be missing or different from the current "addon" value.
    if (groupId) return lineType !== 'garment';
    return lineType === 'addon';
  }

  function physicalCartCount(cart) {
    return (cart.items || []).reduce((sum, line) => {
      if (isNonPhysicalProjectLine(line)) return sum;
      return sum + Number(line.quantity || 0);
    }, 0);
  }

  function updateHeaderCount(cart) {
    const physicalCount = physicalCartCount(cart);
    const countText = String(physicalCount);

    // Update only the actual count text leaves. Never replace or unhide
    // Horizon wrapper/container elements; doing so can detach theme controls
    // and trigger expensive mutation loops.
    document.querySelectorAll('[data-me-physical-cart-count]').forEach((node) => {
      node.textContent = countText;
      node.classList.toggle('hidden', physicalCount === 0);
      const bubble = node.closest('.cart-bubble');
      if (bubble) {
        bubble.classList.toggle('visually-hidden', physicalCount === 0);
        bubble.toggleAttribute('data-maintain-ratio', physicalCount > 0);
      }
    });

    document.querySelectorAll('.theme-drawer__badge').forEach((node) => {
      node.textContent = countText;
      node.classList.toggle('visually-hidden', physicalCount === 0);
      node.toggleAttribute('data-maintain-ratio', physicalCount > 0);
    });

    // This Horizon reference is intentionally visually hidden. Update only
    // its text and never alter its visibility classes.
    document.querySelectorAll('[ref="cartItemCount"]').forEach((node) => {
      node.textContent = String(physicalCount);
    });

    document.querySelectorAll('[data-me-physical-cart-count-label]').forEach((node) => {
      node.textContent = `Total physical products in cart: ${physicalCount}`;
    });
  }

  function updateProjectCard(editor, cart, currency) {
    const data = readData(editor);
    const row = editor.closest('[data-me-personalized-group]');
    if (!row || !data.groupId) return;
    const lines = garmentLines(cart, data.groupId);
    const quantity = lines.reduce((sum, line) => sum + Number(line.quantity || 0), 0);
    const total = projectLines(cart, data.groupId).reduce((sum, line) => sum + Number(line.final_line_price ?? line.line_price ?? 0), 0);

    const count = row.querySelector('[data-me-project-piece-count]');
    if (count) count.textContent = String(quantity);
    const totalNode = row.querySelector('[data-me-project-total]');
    if (totalNode) totalNode.textContent = money(total, currency);
    const config = row.querySelector('[data-me-project-configuration]');
    if (config) config.innerHTML = `${quantity} shirt${quantity === 1 ? '' : 's'} <span aria-hidden="true">·</span> Multiple sizes`;
    const chips = row.querySelector('[data-me-project-size-chips]');
    if (chips) chips.innerHTML = lines.map((line) => `<span class="me-project-size-chip"><strong>${escapeHtml(lineSize(line))}</strong><span aria-hidden="true">×</span><b>${line.quantity}</b></span>`).join('');
  }

  function updateSavings(cart, currency) {
    const metrics = cartMetrics(cart);
    const section = document.querySelector('[data-me-apparel-savings]');
    if (!section) return;
    const badge = section.querySelector('[data-me-savings-badge]');
    const tierMessage = section.querySelector('[data-me-savings-tier-message]');
    const progress = section.querySelector('[data-me-savings-progress]');
    const progressCopy = section.querySelector('[data-me-savings-progress-copy]');
    const regular = section.querySelector('[data-me-savings-regular-total]');
    const apparelTotal = section.querySelector('[data-me-savings-apparel-total]');
    const savedRow = section.querySelector('[data-me-savings-saved-row]');
    const savedAmount = section.querySelector('[data-me-savings-amount]');

    const quoteRequired = metrics.quantity >= 21;
    let target = 21, percent = 100, remaining = 0, nextTier = 10;
    if (metrics.quantity < 6) { target = 6; percent = (metrics.quantity / 6) * 100; remaining = 6 - metrics.quantity; nextTier = 5; }
    else if (metrics.quantity < 11) { target = 11; percent = (metrics.quantity / 11) * 100; remaining = 11 - metrics.quantity; nextTier = 10; }
    else if (metrics.quantity < 21) { target = 21; percent = (metrics.quantity / 21) * 100; remaining = 21 - metrics.quantity; }

    if (badge) {
      badge.textContent = quoteRequired ? 'Quote required' : (metrics.tier ? `${metrics.tier}% earned` : `${metrics.quantity} / 6`);
      badge.classList.toggle('me-apparel-savings__badge--standard', !metrics.tier || quoteRequired);
    }
    if (tierMessage) tierMessage.innerHTML = quoteRequired
      ? '<strong>21+ Custom Bulk Pricing</strong>'
      : metrics.tier
        ? `<strong>${metrics.tier}% Group Savings Earned</strong><span aria-hidden="true">✓</span>`
        : `<strong>${metrics.quantity} eligible apparel item${metrics.quantity === 1 ? '' : 's'}</strong>`;
    if (progress) {
      progress.setAttribute('aria-label', quoteRequired ? 'Managed quote required for 21 or more apparel items' : `${metrics.quantity} of ${target} apparel items toward the next pricing tier`);
      const bar = progress.querySelector('.me-apparel-savings__progress-bar');
      if (bar) bar.style.setProperty('--me-progress', `${Math.min(100, percent)}%`);
    }
    if (progressCopy) progressCopy.textContent = quoteRequired
      ? 'Managed quote review is required for final pricing.'
      : metrics.quantity >= 11
        ? `Only ${remaining} more shirt${remaining === 1 ? '' : 's'} until custom bulk pricing review.`
        : `Only ${remaining} more shirt${remaining === 1 ? '' : 's'} until ${nextTier}% Group Savings.`;
    if (regular) regular.textContent = money(metrics.original, currency);
    if (apparelTotal) apparelTotal.textContent = money(metrics.final, currency);
    if (savedRow) savedRow.hidden = metrics.savings <= 0;
    if (savedAmount) savedAmount.textContent = `−${money(metrics.savings, currency)}`;

    const estimated = document.querySelector('[data-me-cart-estimated-total]');
    if (estimated) {
      const projectedOnly = metrics.actualGroupSavings > 0 ? 0 : metrics.expectedGroupSavings;
      const quoteAdjustment = metrics.quantity >= 21 ? metrics.actualGroupSavings : 0;
      estimated.textContent = money(Math.max(0, Number(cart.total_price || 0) + quoteAdjustment - projectedOnly), currency);
    }
  }

  function updateLiveCart(editor, cart) {
    const data = readData(editor);
    const currency = data.currency || cart.currency || 'USD';
    updateProjectCard(editor, cart, currency);
    updateSavings(cart, currency);
    updateHeaderCount(cart);

    // Reset controls before closing. Do not rebuild the open editor here;
    // the next open reads a fresh cart state. This avoids leaving the panel
    // visually open with stale rows after a successful save.
    setBusy(editor, false, 'Saved. Your cart is updated.');
    setOpen(editor, false);

    document.dispatchEvent(new CustomEvent('moments:cart-updated', { detail: { cart } }));
  }

  async function saveChanges(editor) {
    if (editor.dataset.meBusy === 'true') return;

    const data = readData(editor);
    const rows = currentRows(editor);
    const addition = pendingAddition(editor);
    const desired = rows
      .filter((line) => line.quantity > 0)
      .map(({ size, quantity, variantId }) => ({ size, quantity, variantId }));
    if (addition) desired.push(addition);

    const status = editor.querySelector('[data-me-size-editor-status]');
    if (!desired.length) {
      if (status) status.textContent = 'Keep at least one shirt in this project.';
      return;
    }

    const changedRows = rows.filter((row) => row.quantity !== row.original);
    if (!changedRows.length && !addition) {
      if (status) status.textContent = 'No size changes to save.';
      return;
    }

    setBusy(editor, true, 'Saving all size changes together…');

    try {
      // Read the cart once so garment and paid add-on line keys can be updated
      // together. This avoids one Shopify recalculation per changed size.
      let cart = await cartState();
      const updates = {};

      changedRows.forEach((row) => {
        if (row.key) updates[row.key] = row.quantity;
      });

      const desiredGarmentTotal = desired.reduce(
        (sum, line) => sum + Number(line.quantity || 0),
        0
      );

      // Paid placement add-ons are quantity-based and must stay aligned with
      // the number of garments in this personalization project.
      projectLines(cart, data.groupId)
        .filter((line) => line.properties?.['_ME Line Type'] === 'addon')
        .forEach((line) => {
          updates[line.key] = desiredGarmentTotal;
        });

      if (Object.keys(updates).length) {
        cart = await updateLines(updates);
      }

      if (addition) {
        await addLine({
          id: addition.variantId,
          quantity: addition.quantity,
          properties: updatedProperties(
            data.baseProperties || {},
            desired,
            addition.size,
            addition.quantity,
            data.groupId
          )
        });
        cart = await cartState();

        const addedLine = garmentLines(cart, data.groupId).find((line) =>
          Number(line.variant_id) === Number(addition.variantId) &&
          normalize(lineSize(line)) === normalize(addition.size)
        );
        if (!addedLine) {
          throw new Error('The new size was added to Shopify but could not be linked to this project. Please refresh the cart and try again.');
        }
      }

      updateLiveCart(editor, cart);

      // A cart quantity mutation changes Shopify prices, automatic discounts,
      // group-savings thresholds, add-on quantities, and Liquid-rendered project
      // math. Force one clean cart render so every customer-visible number is
      // authoritative immediately — no manual browser refresh required.
      window.setTimeout(() => window.location.reload(), 120);
    } catch (error) {
      console.error(`[Moments v${VERSION}] Atomic size update failed.`, error);
      setBusy(
        editor,
        false,
        error.message || 'The sizes could not be updated. Your existing project was not removed.'
      );
    }
  }

  document.addEventListener('click', (event) => {
    const editor = event.target.closest('[data-me-size-editor]');
    if (event.target.closest('[data-me-size-editor-toggle]') && editor) { event.preventDefault(); setOpen(editor, editor.querySelector('[data-me-size-editor-panel]')?.hidden !== false); return; }
    if (event.target.closest('[data-me-size-editor-close], [data-me-size-editor-cancel]') && editor) { event.preventDefault(); setOpen(editor, false); return; }
    if (event.target.closest('[data-me-size-minus]') && editor) {
      event.preventDefault();
      const input = event.target.closest('.me-cart-size-editor__stepper')?.querySelector('input');
      if (input) input.value = String(Math.max(0, Number(input.value || 0) - 1));
      return;
    }
    if (event.target.closest('[data-me-size-plus]') && editor) {
      event.preventDefault();
      const input = event.target.closest('.me-cart-size-editor__stepper')?.querySelector('input');
      if (input) input.value = String(Math.max(0, Number(input.value || 0)) + 1);
      return;
    }
    if (event.target.closest('[data-me-size-editor-update]') && editor) { event.preventDefault(); saveChanges(editor); return; }
    const tierToggle = event.target.closest('[data-me-savings-info-toggle]');
    if (tierToggle) {
      event.preventDefault();
      const panel = tierToggle.closest('[data-me-savings-info]')?.querySelector('[data-me-savings-info-panel]');
      if (panel) {
        const open = panel.hidden;
        document.querySelectorAll('[data-me-savings-info-panel]').forEach((item) => { item.hidden = true; });
        panel.hidden = !open;
        tierToggle.setAttribute('aria-expanded', String(open));
      }
      return;
    }
  });

  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-me-savings-info]')) return;
    document.querySelectorAll('[data-me-savings-info-panel]').forEach((panel) => { panel.hidden = true; });
    document.querySelectorAll('[data-me-savings-info-toggle]').forEach((button) => button.setAttribute('aria-expanded', 'false'));
  });

  document.addEventListener('input', (event) => {
    const input = event.target.closest('[data-me-size-input], [data-me-add-size-input]');
    if (input) input.value = String(Math.max(0, Number.parseInt(input.value || '0', 10) || 0));
  });
})();
